/* Maison Misu — the hero reveal, the room switcher, the colour flip, the buy-block swatches and the phone shop bar.
   Plain JS, no dependencies. Everything degrades to a static page without it. */
(() => {
  const root = document.documentElement;
  root.classList.add('mm-js');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
  const smooth = (e0, e1, x) => { const t = clamp((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t); };
  const inOut = (u) => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);

  let heroes = [];
  let heroDone = true;

  /* ---------- header height, so hero copy clears it ---------- */
  function setHeaderHeight() {
    const h = document.querySelector('.header-wrapper') || document.querySelector('.section-header');
    if (h) root.style.setProperty('--mm-header-h', `${h.offsetHeight}px`);
  }

  /* ---------- hero: the photo starts on the plant and pulls back to show the cat leaving the box ---------- */
  function heroFrame() {
    let done = heroes.length === 0;
    for (const h of heroes) {
      const track = h.querySelector('.mm-hero__track');
      const z0 = parseFloat(h.dataset.zoom || '2');
      let p = 1;
      if (!reduce.matches && track) {
        const r = track.getBoundingClientRect();
        const total = Math.max(1, track.offsetHeight - window.innerHeight);
        p = clamp(-r.top / total);
      }
      const z = z0 + (1 - z0) * smooth(0.04, 0.55, p);
      h.style.setProperty('--z', z.toFixed(4));
      h.style.setProperty('--a', (1 - smooth(0.2, 0.34, p)).toFixed(3));
      h.style.setProperty('--b', smooth(0.36, 0.5, p).toFixed(3));
      h.style.setProperty('--c', smooth(0.5, 0.62, p).toFixed(3));
      if (p > 0.55) done = true;
    }
    if (done !== heroDone) { heroDone = done; stickyUpdate(); }
  }

  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { ticking = false; heroFrame(); });
  }

  /* ---------- rooms: tap a room and it ripples out from the box ---------- */
  function initRooms(scope) {
    scope.querySelectorAll('[data-mm-rooms]').forEach((rooms) => {
      if (rooms.dataset.ready) return;
      rooms.dataset.ready = '1';
      const frame = rooms.querySelector('.mm-rooms__frame');
      const imgs = [...rooms.querySelectorAll('.mm-rooms__slot')];
      const tabs = [...rooms.querySelectorAll('.mm-rooms__tab')];
      const fx = parseFloat(rooms.dataset.fx || '60') / 100;
      const fy = parseFloat(rooms.dataset.fy || '63') / 100;
      let cur = 0;
      let raf = 0;

      const settle = (i) => {
        imgs.forEach((im, k) => {
          im.style.webkitMaskImage = im.style.maskImage = 'none';
          im.style.zIndex = k === i ? 2 : 0;
          im.hidden = k !== i;
        });
      };

      function show(i) {
        if (i === cur || !imgs[i]) return;
        tabs.forEach((t, k) => t.setAttribute('aria-pressed', String(k === i)));
        const prev = imgs[cur], next = imgs[i];
        cancelAnimationFrame(raf);
        imgs.forEach((im) => { im.style.zIndex = 0; });
        prev.hidden = false; prev.style.zIndex = 1;
        next.hidden = false; next.style.zIndex = 2;
        const pic = next.querySelector('img'); if (pic) pic.loading = 'eager';
        cur = i;
        if (reduce.matches) { settle(i); return; }
        const W = frame.clientWidth, H = frame.clientHeight, cx = fx * W, cy = fy * H;
        const R = Math.hypot(Math.max(cx, W - cx), Math.max(cy, H - cy)) * 1.12;
        const t0 = performance.now(), D = 840;
        const step = (now) => {
          const u = clamp((now - t0) / D), r = inOut(u) * R, f = Math.max(36, r * 0.32);
          const g = `radial-gradient(circle at ${cx.toFixed(1)}px ${cy.toFixed(1)}px, #000 ${Math.max(0, r - f).toFixed(1)}px, transparent ${r.toFixed(1)}px)`;
          next.style.webkitMaskImage = g; next.style.maskImage = g;
          if (u < 1) raf = requestAnimationFrame(step); else settle(i);
        };
        raf = requestAnimationFrame(step);
      }

      tabs.forEach((t, i) => t.addEventListener('click', () => show(i)));
      // swipe between rooms on touch screens
      let x0 = null;
      frame.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      frame.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0; x0 = null;
        if (Math.abs(dx) > 40) show((cur + (dx < 0 ? 1 : imgs.length - 1)) % imgs.length);
      });
      // warm the other rooms once the section is close
      const io = new IntersectionObserver((es) => {
        if (es.some((e) => e.isIntersecting)) { rooms.querySelectorAll('.mm-rooms__slot img').forEach((im) => { im.loading = 'eager'; }); io.disconnect(); }
      }, { rootMargin: '600px 0px' });
      io.observe(rooms);
    });
  }

  /* ---------- colour sweep: a soft edge runs down a photo layer, like the colour change in the ads ----------
     reveal=true shows the layer from the top down; reveal=false clears it from the top down.
     from: where the edge starts, in % of the layer height (the plant is the same in both photos, so start at the box). */
  function sweep(el, reveal, from, done) {
    cancelAnimationFrame(el._mmSweep);
    const finish = () => { el.style.webkitMaskImage = el.style.maskImage = 'none'; if (done) done(); };
    if (reduce.matches) { finish(); return; }
    const t0 = performance.now(), D = 640, F = 14;
    const frame = (now) => {
      const u = clamp((now - t0) / D), e = 1 - Math.pow(1 - u, 2.2), y = from + (100 + F - from) * e;
      const g = reveal
        ? `linear-gradient(to bottom, #000 ${(y - F).toFixed(2)}%, transparent ${y.toFixed(2)}%)`
        : `linear-gradient(to bottom, transparent ${(y - F).toFixed(2)}%, #000 ${y.toFixed(2)}%)`;
      el.style.webkitMaskImage = g; el.style.maskImage = g;
      if (u < 1) el._mmSweep = requestAnimationFrame(frame); else finish();
    };
    el._mmSweep = requestAnimationFrame(frame);
  }
  const colourOf = (s) => String(s || '').trim().toLowerCase();

  /* ---------- colours section: tap Cream or Black and the box changes in place ---------- */
  function initColours(scope) {
    scope.querySelectorAll('[data-mm-colours]').forEach((sec) => {
      if (sec.dataset.ready) return;
      sec.dataset.ready = '1';
      const top = sec.querySelector('[data-mm-colours-top]');
      const btns = [...sec.querySelectorAll('[data-colour]')];
      if (!top || btns.length < 2) return;
      let cur = 0, touched = false;
      const set = (i, user) => {
        if (user) touched = true;
        if (i !== cur) {
          cur = i;
          btns.forEach((b, k) => b.setAttribute('aria-pressed', String(k === i)));
          if (i === 1) { top.classList.add('is-on'); sweep(top, true, 30); }
          else sweep(top, false, 30, () => { if (cur === 0) top.classList.remove('is-on'); });
        }
        if (user) document.dispatchEvent(new CustomEvent('mm:colour', { detail: { name: btns[i].dataset.colour } }));
      };
      btns.forEach((b, i) => b.addEventListener('click', () => set(i, true)));
      // show the change once, the first time the section is properly in view
      if (!reduce.matches && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => {
          if (es.some((e) => e.isIntersecting)) {
            io.disconnect();
            setTimeout(() => { if (!touched) set(1, false); }, 1100);
          }
        }, { threshold: 0.6 });
        io.observe(top);
      }
    });
  }

  /* ---------- buy block: colour swatches pick the variant and sweep the photo over to it ---------- */
  function initBuy(scope) {
    scope.querySelectorAll('[data-mm-buy]').forEach((buy) => {
      if (buy.dataset.ready) return;
      buy.dataset.ready = '1';
      const radios = [...buy.querySelectorAll('[data-mm-swatches] input[type="radio"]')];
      if (!radios.length) return;
      const idInput = buy.querySelector('form input[name="id"]');
      const nameEl = buy.querySelector('[data-mm-swatch-name]');
      const priceEl = buy.querySelector('[data-mm-buy-price]');
      const btn = buy.querySelector('.product-form__submit');
      const btnLabel = btn ? btn.querySelector('span') : null;
      const photos = [...buy.querySelectorAll('[data-variant-photo]')];
      let shown = photos.find((p) => p.classList.contains('is-active'));
      const apply = (r) => {
        if (idInput) idInput.value = r.value;
        if (nameEl) nameEl.textContent = r.dataset.name;
        if (priceEl && r.dataset.price) priceEl.textContent = r.dataset.price;
        radios.forEach((x) => x.closest('.mm-swatch').classList.toggle('is-checked', x === r));
        const ok = r.dataset.available === 'true';
        if (btn) { btn.disabled = !ok; if (btnLabel && btn.dataset.add) btnLabel.textContent = ok ? btn.dataset.add : btn.dataset.sold; }
        const next = photos.find((p) => p.dataset.variantPhoto === r.value);
        if (next && next !== shown) {
          const prev = shown; shown = next;
          photos.forEach((p) => { p.style.zIndex = p === next ? 2 : p === prev ? 1 : 0; });
          next.classList.add('is-active');
          const img = next.querySelector('img'); if (img) img.loading = 'eager';
          sweep(next, true, 30, () => { photos.forEach((p) => { if (p !== shown) p.classList.remove('is-active'); }); });
        }
      };
      radios.forEach((r) => r.addEventListener('change', () => apply(r)));
      // a colour picked in the colours section picks the same one here
      document.addEventListener('mm:colour', (e) => {
        const want = colourOf(e.detail && e.detail.name);
        const r = radios.find((x) => colourOf(x.dataset.colour) === want);
        if (r && !r.checked) { r.checked = true; apply(r); }
      });
      // load the other colour's photo once the block is close, so the first switch is instant
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => {
          if (es.some((e) => e.isIntersecting)) { buy.querySelectorAll('[data-variant-photo] img').forEach((im) => { im.loading = 'eager'; }); io.disconnect(); }
        }, { rootMargin: '600px 0px' });
        io.observe(buy);
      }
    });
  }

  /* ---------- product page: a colour picked in the colours section picks it in Dawn's variant picker too ---------- */
  document.addEventListener('mm:colour', (e) => {
    const want = colourOf(e.detail && e.detail.name);
    document.querySelectorAll('variant-selects input[type="radio"]').forEach((inp) => {
      if (colourOf(inp.value) === want && !inp.checked) inp.click();
    });
    document.querySelectorAll('variant-selects select').forEach((sel) => {
      const opt = [...sel.options].find((o) => colourOf(o.value) === want);
      if (opt && sel.value !== opt.value) { sel.value = opt.value; sel.dispatchEvent(new Event('change', { bubbles: true })); }
    });
  });

  /* ---------- phone shop bar: shows once the reveal has landed, steps aside for the buy block and footer ---------- */
  let sticky = null, buyVisible = false, footerVisible = false, stickyIO = null;
  function stickyUpdate() {
    if (!sticky) return;
    const on = heroDone && !buyVisible && !footerVisible;
    sticky.classList.toggle('is-on', on);
    sticky.inert = !on;
  }
  function initSticky() {
    sticky = document.querySelector('[data-mm-sticky]');
    if (!sticky) return;
    if (stickyIO) stickyIO.disconnect();
    const buy = document.querySelector('[data-mm-buy]');
    const footer = document.querySelector('.footer') || document.querySelector('footer');
    stickyIO = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (e.target === buy) buyVisible = e.isIntersecting;
        if (e.target === footer) footerVisible = e.isIntersecting;
      });
      stickyUpdate();
    }, { threshold: 0.1 });
    if (buy) stickyIO.observe(buy);
    if (footer) stickyIO.observe(footer);
  }

  function init(scope = document) {
    heroes = [...document.querySelectorAll('[data-mm-hero]')];
    heroDone = heroes.length === 0;
    setHeaderHeight();
    initRooms(scope);
    initColours(scope);
    initBuy(scope);
    initSticky();
    heroFrame();
    stickyUpdate();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => { setHeaderHeight(); onScroll(); });
  if (reduce.addEventListener) reduce.addEventListener('change', onScroll);
  // theme editor: sections are re-rendered in place
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
